import React, { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getTabConfigForPath } from '@/config/nav'
import { useLanguage } from '@/contexts/LanguageContext'
import { getIconComponent } from '@/lib/iconMapper'
import { LayoutDashboard } from 'lucide-react'
import { cn, Tabs, TabsList, TabsTrigger } from '@appic/ui-components'
import { usePermissions } from '@appic/hooks'

interface SubpageTabsHeaderProps {
  className?: string
}

/**
 * SubpageTabsHeader component - GitHub-style subpage tabs in header
 * Renders tabs below the topbar, sticky at the top
 * Only shows when there are tabs for the current route
 */
export const SubpageTabsHeader: React.FC<SubpageTabsHeaderProps> = ({ className }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { canAccess, userRole } = usePermissions()
  const getTabValue = (tab: { id?: string; href: string }) =>
    tab.id || tab.href.replace(/[^a-zA-Z0-9-_]/g, '-')

  // Get tab configuration for current path
  const tabConfig = getTabConfigForPath(location.pathname)

  // Filter tabs by permission
  const visibleTabs = useMemo(() => {
    if (!tabConfig?.tabs) return []
    return tabConfig.tabs.filter(tab => {
      if (tab.permission && !canAccess(tab.permission)) return false
      if (tab.hideForPermission && canAccess(tab.hideForPermission)) return false
      if (tab.roles && !tab.roles.includes(userRole)) return false
      return true
    })
  }, [tabConfig, canAccess, userRole])

  // If no tabs for this path, don't render anything
  if (!tabConfig || visibleTabs.length === 0) {
    return null
  }

  // Find active tab based on current path — prefer exact match, then longest prefix
  const normalizedPath = location.pathname.replace(/\/$/, '') || '/'
  /** Full-page doc reader: hide ATC sub-tabs on small viewports so content is edge-to-edge. */
  const docDetailFullBleedMobile =
    /^\/atc\/docs\/[^/]+$/.test(normalizedPath) ||
    /^\/atc\/bridge\/wiki\/[^/]+$/.test(normalizedPath)
  // Resolve "active" by tab.pathPrefix (when present) or tab.href, picking the
  // longest match. Group-tab patterns (Commander Bridge IA) set pathPrefix to
  // the group root so sibling sub-routes pin the same outer tab.
  const tabPrefix = (tab: { href: string; pathPrefix?: string }) =>
    (tab.pathPrefix ?? tab.href).replace(/\/$/, '')
  const activeTab = visibleTabs.find(tab => normalizedPath === tabPrefix(tab))
    || [...visibleTabs]
        .filter(tab => normalizedPath.startsWith(tabPrefix(tab) + '/'))
        .sort((a, b) => tabPrefix(b).length - tabPrefix(a).length)[0]
    || visibleTabs[0]
  
  const handleTabChange = (value: string) => {
    const tab = visibleTabs.find(t => getTabValue(t) === value)
    if (tab) {
      navigate(tab.href)
    }
  }
  
  return (
    <div 
      className={cn(
        "w-full border-t flex-shrink-0 sticky top-0 z-10",
        "bg-background/95 backdrop-blur",
        "border-border bg-card max-h-15 overflow-hidden",
        docDetailFullBleedMobile && "hidden sm:block",
        className
      )}
    >
      <div className="w-full content-padding-x">
        <Tabs value={getTabValue(activeTab)} onValueChange={handleTabChange}>
          <TabsList 
            className="w-full justify-start border-0 rounded-none bg-transparent p-0 h-auto overflow-x-auto overflow-y-hidden border-border max-h-15"
          >
            {visibleTabs.map((tab) => {
              const Icon = getIconComponent(tab.icon, LayoutDashboard)
              const isActive = getTabValue(tab) === getTabValue(activeTab)
              const tabValue = getTabValue(tab)
              
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tabValue}
                  className={cn(
                    "rounded-none border-b-2 border-transparent",
                    "px-4 py-3 text-sm font-medium whitespace-nowrap",
                    "transition-colors min-w-fit",
                    "hover:bg-transparent hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    "focus-visible:ring-offset-background",
                    "bg-transparent",
                    isActive ? "text-accent border-b-2 border-accent" : "text-muted-foreground"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">{t(tab.labelKey)}</span>
                  </div>
                </TabsTrigger>
              )
            })}
          </TabsList>
        </Tabs>
      </div>
    </div>
  )
}

